import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  RiAddLine,
  RiArrowRightUpLine,
  RiDeleteBinLine,
  RiPencilLine,
  RiPlugLine,
  RiPulseLine,
  RiShutDownLine,
  RiToggleLine,
} from '@remixicon/react';
import { CONNECTOR_STATUSES, CONNECTOR_TYPES, type AccessibleTenant, type ConnectorDto, type ConnectorType, type MeResponse } from '@atnt/shared';
import { ContentPanel, NoAccess, PageHeader, PageLayout } from '@/app/shell';
import { Button, confirm, DataTable, EmptyState, Select, toast, Tooltip, type DataTableColumn, type MenuItemSpec } from '@/shared/ui';
import { ErrorBanner, NoResults, RegistryToolbar } from '@/features/devices/components/parts';
import { matches } from '@/features/devices/format';
import { useHotkey } from '@/features/settings/hotkeys';
import { useTableDensity } from '@/features/settings/preferences';
import { useConnectors, useConnectorsContext, useDeleteConnector, useTenantDetails, useTestConnector, useToggleConnector } from './api';
import type { ConnectorsContext } from './backend';
import { ConnectorPanel } from './components/ConnectorPanel';
import { BoundCount, ConnectorStatusBadge, RelativeTime, ScopeBadge, TypeChip } from './components/parts';
import { connectorRights, type ConnectorRights } from './permissions';
import { sourceHost } from './util';

export interface ConnectorsSearch {
  /** `new` or a connector id (edit) */
  panel?: string;
  /** preselected type of a new connector (opens the form step) */
  type?: string;
}

const ALL = '__all__';

/**
 * "Konnektorlar" — data sources of the reservoir's sensors (03-modules §1.2). Organization connectors (the
 * O'zbekgidroenergo unified server, shared by all reservoirs) and the reservoir's own connectors in one registry table:
 * name + type, scope, status, bound / discovered sensors, last sync; create / edit in the docked side panel
 * (`?panel=new|<id>`), row menu Ochish · Tekshirish · Yoqish/O'chirish · Tahrirlash · O'chirish.
 */
export function ConnectorsPage({
  tenant,
  me,
  search: url,
  onSearch,
}: {
  tenant: AccessibleTenant;
  me: MeResponse;
  search: ConnectorsSearch;
  onSearch: (next: ConnectorsSearch) => void;
}) {
  const rights = connectorRights(tenant, me);
  if (!rights.read) return <NoAccess />;
  return <ConnectorsRegistry tenant={tenant} me={me} rights={rights} url={url} onSearch={onSearch} />;
}

function ConnectorsRegistry({
  tenant,
  me,
  rights,
  url,
  onSearch,
}: {
  tenant: AccessibleTenant;
  me: MeResponse;
  rights: ConnectorRights;
  url: ConnectorsSearch;
  onSearch: (next: ConnectorsSearch) => void;
}) {
  const { t } = useTranslation('connectors');
  const navigate = useNavigate();
  const ctx = useConnectorsContext(tenant, me);
  const density = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const connectors = useConnectors(ctx);
  const details = useTenantDetails(tenant.id);

  const all = connectors.data ?? [];
  const rows = all.filter(
    (c) =>
      (matches(c.name, search) || matches(t(`types.${c.type}.name`), search) || matches(sourceHost(c.config), search)) &&
      (type === ALL || c.type === type) &&
      (status === ALL || c.status === status),
  );
  const filtered = Boolean(search) || type !== ALL || status !== ALL;
  const editing = url.panel && url.panel !== 'new' ? all.find((c) => c.id === url.panel) : undefined;
  const panelOpen = (url.panel === 'new' && rights.create) || (Boolean(editing) && rights.canEdit(editing!));
  const openPanel = (panel: string | undefined, preset?: ConnectorType) => onSearch({ panel, type: preset });
  const open = (c: ConnectorDto) => void navigate({ to: '/t/$tenantSlug/connectors/$connectorId', params: { tenantSlug: tenant.slug, connectorId: c.id } });

  useHotkey('create', () => openPanel('new'), { enabled: rights.create && !panelOpen });
  useHotkey('search', () => searchRef.current?.focus());
  useHotkey('refresh', () => void connectors.refetch());

  const actions = useRowActions(ctx, rights, { onOpen: open, onEdit: (c) => openPanel(c.id) });

  const columns: DataTableColumn<ConnectorDto>[] = [
    {
      id: 'name',
      header: t('list.name'),
      minWidth: 260,
      cell: (c) => (
        <span className="flex min-w-0 items-center gap-8">
          <TypeChip type={c.type} size={20} />
          <span className="truncate font-medium text-fg">{c.name}</span>
          <span className="min-w-0 shrink truncate text-xs text-fg-tertiary">{sourceHost(c.config)}</span>
        </span>
      ),
    },
    { id: 'type', header: t('list.type'), width: 180, tone: 'secondary', cell: (c) => <span className="truncate">{t(`types.${c.type}.name`)}</span> },
    { id: 'scope', header: t('list.scope'), width: 130, cell: (c) => <ScopeBadge scope={c.scope} /> },
    {
      id: 'status',
      header: t('list.status'),
      width: 160,
      cell: (c) =>
        c.status === 'error' && c.lastError ? (
          <Tooltip content={c.lastError}>
            <span className="inline-flex">
              <ConnectorStatusBadge status={c.status} />
            </span>
          </Tooltip>
        ) : (
          <ConnectorStatusBadge status={c.status} />
        ),
    },
    { id: 'sensors', header: t('list.sensors'), width: 170, cell: (c) => <BoundCount c={c} /> },
    { id: 'sync', header: t('list.lastSync'), width: 170, tone: 'secondary', cell: (c) => <RelativeTime ts={c.lastSyncAt} /> },
  ];

  const filter = (label: string, value: string, onChange: (v: string) => void, options: Array<{ value: string; label: string }>, allLabel: string) => (
    <Select<string> aria-label={label} className="w-200" value={value} onValueChange={(v) => onChange(v ?? ALL)} options={[{ value: ALL, label: allLabel }, ...options]} />
  );

  return (
    <>
      <PageHeader
        title={t('list.title')}
        actions={
          rights.create ? (
            <Button size="sm" leftIcon={RiAddLine} onClick={() => openPanel('new')}>
              {t('list.create')}
            </Button>
          ) : null
        }
      />
      <PageLayout
        aside={
          panelOpen ? (
            <ConnectorPanel
              key={url.panel}
              ctx={ctx}
              rights={rights}
              connector={editing}
              initialType={CONNECTOR_TYPES.includes(url.type as ConnectorType) ? (url.type as ConnectorType) : undefined}
              siteCode={details.data?.externalSiteId ?? null}
              unifiedExists={all.some((c) => c.type === 'atnt_unified')}
              onClose={() => openPanel(undefined)}
              onSaved={(saved, created) => {
                if (created) open(saved);
                else openPanel(undefined);
              }}
            />
          ) : null
        }
      >
        <ContentPanel>
          <RegistryToolbar search={search} onSearch={setSearch} placeholder={t('list.search')} total={connectors.data ? rows.length : null} searchRef={searchRef}>
            {filter(t('list.typeFilter'), type, setType, CONNECTOR_TYPES.map((v) => ({ value: v, label: t(`types.${v}.name`) })), t('list.allTypes'))}
            {filter(t('list.statusFilter'), status, setStatus, CONNECTOR_STATUSES.map((v) => ({ value: v, label: t(`statuses.${v}`) })), t('list.allStatuses'))}
          </RegistryToolbar>
          {connectors.isError ? (
            <ErrorBanner onRetry={() => void connectors.refetch()} />
          ) : (
            <DataTable
              aria-label={t('list.title')}
              columns={columns}
              rows={rows}
              getRowId={(c) => c.id}
              indexColumn={{ header: t('index'), format: (i) => `${i + 1}.` }}
              rowActions={actions}
              actionsHeader={t('table.actions')}
              actionsLabel={t('table.actionsLabel')}
              onRowClick={open}
              loading={connectors.isLoading}
              skeletonRows={3}
              density={density}
              activeRowId={editing?.id}
              minWidth={1180}
              empty={
                filtered ? (
                  <NoResults
                    onClear={() => {
                      setSearch('');
                      setType(ALL);
                      setStatus(ALL);
                    }}
                  />
                ) : (
                  <EmptyState
                    icon={RiPlugLine}
                    title={t('list.empty')}
                    description={t('list.emptyHint')}
                    action={rights.create ? { label: t('list.create'), icon: RiAddLine, onClick: () => openPanel('new') } : undefined}
                  />
                )
              }
            />
          )}
        </ContentPanel>
      </PageLayout>
    </>
  );
}

/**
 * Row / header actions of a connector, shared by the registry and the detail page: Ochish, Tekshirish,
 * Yoqish / O'chirish, Tahrirlash, O'chirish (confirm). Hidden when the user lacks the right.
 */
export function useRowActions(
  ctx: ConnectorsContext,
  rights: ConnectorRights,
  handlers: { onOpen?: (c: ConnectorDto) => void; onEdit?: (c: ConnectorDto) => void; onDeleted?: (c: ConnectorDto) => void },
) {
  const { t } = useTranslation('connectors');
  const test = useTestConnector(ctx);
  const toggle = useToggleConnector(ctx);
  const remove = useDeleteConnector(ctx);

  const runTest = async (c: ConnectorDto) => {
    try {
      const r = await test.mutateAsync({ id: c.id });
      if (r.ok) toast.success(t('test.ok'), { subtitle: [c.name, r.latencyMs != null ? t('test.latency', { ms: r.latencyMs }) : null].filter(Boolean).join(' · ') });
      else toast.error(t('test.failed'), { subtitle: r.message ?? c.name });
    } catch (err) {
      toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };
  const runToggle = async (c: ConnectorDto) => {
    try {
      const next = await toggle.mutateAsync({ id: c.id, enabled: !c.enabled });
      if (next.status === 'error') toast.warning(t('menu.enabled'), { subtitle: next.lastError ?? c.name });
      else toast.success(c.enabled ? t('menu.disabled') : t('menu.enabled'), { subtitle: c.name });
    } catch (err) {
      toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };
  const runDelete = async (c: ConnectorDto) => {
    const ok = await confirm({
      title: t('menu.deleteTitle'),
      description: c.scope === 'organization' ? t('menu.deleteOrgText', { name: c.name }) : t('menu.deleteText', { name: c.name }),
      tone: 'danger',
      confirmLabel: t('menu.delete'),
      cancelLabel: t('cancel'),
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(c.id);
      toast.success(t('menu.deleted'), { subtitle: c.name });
      handlers.onDeleted?.(c);
    } catch (err) {
      toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };

  return (c: ConnectorDto): MenuItemSpec[] => {
    const items: MenuItemSpec[] = [];
    if (handlers.onOpen) items.push({ key: 'open', label: t('menu.open'), icon: RiArrowRightUpLine, onSelect: () => handlers.onOpen!(c) });
    if (rights.operate) items.push({ key: 'test', label: t('menu.test'), icon: RiPulseLine, disabled: !c.enabled || c.status === 'checking', onSelect: () => void runTest(c) });
    if (rights.canEdit(c)) {
      items.push({ key: 'toggle', label: c.enabled ? t('menu.disable') : t('menu.enable'), icon: c.enabled ? RiShutDownLine : RiToggleLine, onSelect: () => void runToggle(c) });
      if (handlers.onEdit) items.push({ key: 'edit', label: t('menu.edit'), icon: RiPencilLine, onSelect: () => handlers.onEdit!(c) });
    }
    if (rights.canDelete(c)) {
      items.push({ type: 'separator', key: 'sep' }, { key: 'delete', label: t('menu.delete'), icon: RiDeleteBinLine, danger: true, onSelect: () => void runDelete(c) });
    }
    return items;
  };
}
